import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { CheckCircleFilled } from "@ant-design/icons";
import { ReactComponent as VerticalDotsIcon } from "../../assets/vertical_dots.svg";
import { ReactComponent as HeartIcon } from "../../assets/heart.svg";
import { ReactComponent as HeartOutlinedIcon } from "../../assets/heart_outlined.svg";
import { ReactComponent as MessageIcon } from "../../assets/comment.svg";
import { ReactComponent as ShareIcon } from "../../assets/share.svg";
import { useAppDispatch, useAppSelector } from "../../store";
import {
  deletePostThunk,
  getCommentsThunk,
  reactToPostThunk,
} from "../../store/actions/social.action";
import { CommentModal } from "./CommentModal";
import { MediaContent } from "./MediaContent";
import { renderWithHashtags } from "../../core/utils/renderWithHashtags";
import { timeAgo } from "../../core/utils/timeAgo";
import { formatNumber } from "../../core/utils/formatNumber";
import Avatar from "boring-avatars";
import { Dropdown, Menu, MenuProps } from "antd";
import { message } from "antd";
import { useNavigate } from "react-router-dom";
import AuthGuard from "../AuthGuard";

export interface PostType {
  _id: string;
  author: string;
  avatar: string;
  verified: boolean;
  title: string;
  content: string;
  videos: string[];
  images: string[];
  likes: number;
  commentCount: number;
  createdAt: string;
  userReaction: string;
  user: {
    _id: string;
    avatarUrl: string;
    firstName: string;
    lastName: string;
    email: string;
    paymentMode: number;
  };
  reactionSummary: {
    _id: string;
    count: number;
  }[];
}

interface PostCardProps {
  post: PostType;
  hideCommentModal?: boolean;
  onCommentClick?: () => void;
  onDelete?: (postId: string) => Promise<void>;
}

export const PostCard = ({
  post,
  hideCommentModal,
  onCommentClick,
  onDelete,
}: PostCardProps) => {
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [optimisticLike, setOptimisticLike] = useState(post.userReaction);
  const [optimisticLikeCount, setOptimisticLikeCount] = useState(
    post.reactionSummary[0]?.count || 0
  );
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.authReducer.me);
  const isOwner =
    currentUser?.email === post.user.email ||
    currentUser?.email?.endsWith("@kardo.ai");
  const navigate = useNavigate();

  const handleDelete = async () => {
    if (onDelete) {
      await onDelete(post._id);
    } else {
      try {
        await dispatch(deletePostThunk({ postId: post._id }));
        message.success("Post deleted successfully");
      } catch (error) {
        message.error("Failed to delete post");
      }
    }
  };

  const fetchComments = async (postId: string) => {
    await dispatch(
      getCommentsThunk({
        postId: postId,
        page: 1,
        limit: 5,
      })
    );
  };

  const reactToPost = async (postId: string) => {
    // Optimistically update the UI
    setOptimisticLike(optimisticLike ? "" : "like");
    setOptimisticLikeCount((prev) => (optimisticLike ? prev - 1 : prev + 1));

    try {
      await dispatch(
        reactToPostThunk({
          postId: postId,
        })
      );
    } catch (error) {
      // Revert the optimistic update if the request fails
      setOptimisticLike(post.userReaction);
      setOptimisticLikeCount(post.reactionSummary[0]?.count || 0);
    }
  };

  const handleShare = async () => {
    const postUrl = `${window.location.origin}/post/${post._id}`;
    try {
      await navigator.clipboard.writeText(postUrl);
      message.success("Post URL copied to clipboard");
    } catch (err) {
      message.error("Failed to copy URL");
    }
  };

  useEffect(() => {
    setOptimisticLike(post.userReaction);
    setOptimisticLikeCount(post.reactionSummary[0]?.count || 0);
  }, [post.userReaction, post.reactionSummary]);

  return (
    <>
      <PostCardWrapper>
        <PostHeader>
          {post.user.avatarUrl ? (
            <img
              style={{
                width: 52,
                height: 52,
                borderRadius: "50%",
                objectFit: "cover",
              }}
              src={post.user.avatarUrl}
              onClick={(event) => {
                event.stopPropagation();
                navigate(`/user/${post.user?._id}`);
              }}
            />
          ) : (
            <Avatar
              name={post.user.firstName + " " + post.user.lastName}
              variant="beam"
              size={52}
              onClick={(event) => {
                event.stopPropagation();
                navigate(`/user/${post.user?._id}`);
              }}
            />
          )}
          <PostUser>
            <UsernameRow>
              <Username
                onClick={(event) => {
                  event.stopPropagation();
                  navigate(`/user/${post.user?._id}`);
                }}
              >
                {post.user.firstName} {post.user.lastName}
              </Username>
              {post.user.paymentMode === 4 && <Verified />}
            </UsernameRow>
            <PostTime>{timeAgo(post.createdAt)}</PostTime>
          </PostUser>
          <div style={{ marginLeft: "auto" }}>
            {isOwner && (
              <Dropdown
                menu={{
                  items: [
                    {
                      key: "delete",
                      label: "Delete",
                      onClick: (e) => {
                        e.domEvent.stopPropagation();
                        handleDelete();
                      },
                      style: {
                        color: "#fff",
                        fontWeight: 600,
                      },
                    },
                  ],
                  style: {
                    backgroundColor: "#1a1a1a",
                    borderRadius: "8px",
                    zIndex: 1000,
                  },
                }}
                trigger={["click"]}
                placement="bottomRight"
              >
                <div
                  style={{
                    marginLeft: "auto",
                    color: "#bdbdbd",
                    fontSize: 22,
                    cursor: "pointer",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <VerticalDotsIcon style={{ width: 18 }} />
                </div>
              </Dropdown>
            )}
          </div>
        </PostHeader>
        {post.content && (
          <PostContent>{renderWithHashtags(post.content)}</PostContent>
        )}
        {(post.images.length > 0 || post.videos.length > 0) && (
          <div style={{ height: "320px", overflow: "hidden" }}>
            <MediaContent
              files={[
                ...post.images.map((url) => ({ url, type: "image/jpeg" })),
                ...post.videos.map((url) => ({ url, type: "video/mp4" })),
              ]}
            />
          </div>
        )}
        <PostActions onClick={(e) => e.stopPropagation()}>
          <ActionGroup>
            <AuthGuard>
              <ActionButton
                onClick={() => {
                  if (!currentUser) return;
                  reactToPost(post._id);
                }}
              >
                {optimisticLike ? (
                  <HeartIcon style={{ width: 24, height: 24 }} />
                ) : (
                  <HeartOutlinedIcon style={{ width: 24, height: 24 }} />
                )}
              </ActionButton>
            </AuthGuard>
            {!hideCommentModal && (
              <ActionButton
                onClick={() => {
                  if (onCommentClick) {
                    onCommentClick();
                  } else {
                    fetchComments(post._id);
                    setIsCommentModalOpen(true);
                  }
                }}
              >
                <MessageIcon style={{ width: 24, height: 24 }} />
              </ActionButton>
            )}
            <ActionButton
              onClick={(e) => {
                e.stopPropagation();
                handleShare();
              }}
            >
              <ShareIcon style={{ width: 20, height: 20 }} />
            </ActionButton>
          </ActionGroup>
          <PostStats>
            <StatItem>
              <span style={{ fontWeight: 600 }}>
                {formatNumber(optimisticLikeCount)}
              </span>{" "}
              likes
            </StatItem>
            <StatItem
              onClick={() => !hideCommentModal && navigate(`/post/${post._id}`)}
            >
              <span style={{ fontWeight: 600 }}>
                {formatNumber(post.commentCount || 0)}
              </span>{" "}
              <CommentsText>comments</CommentsText>
            </StatItem>
          </PostStats>
        </PostActions>
      </PostCardWrapper>

      {!hideCommentModal && !onCommentClick && (
        <CommentModal
          isOpen={isCommentModalOpen}
          onClose={() => setIsCommentModalOpen(false)}
          post={post}
        />
      )}
    </>
  );
};

const PostCardWrapper = styled.div`
  background: #282828;
  border-radius: 24px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.18);
  overflow: hidden;
  padding: 1.2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  box-sizing: border-box;
  transition: transform 0.2s;
`;

const PostHeader = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const PostUser = styled.div`
  display: flex;
  flex-direction: column;
  margin: auto 0;
`;

const UsernameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const Username = styled.span`
  color: #fff;
  font-weight: 600;
  font-size: 1rem;

  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const Verified = styled(CheckCircleFilled)`
  color: #ff8e53;
  font-size: 1rem;
`;

const PostTime = styled.span`
  color: #bdbdbd;
  font-size: 0.9rem;
`;

const PostContent = styled.div`
  color: #d1d5db;
  font-size: 1rem;
  word-wrap: break-word;
  white-space: pre-wrap;
  overflow-wrap: break-word;
`;

const PostActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ActionGroup = styled.div`
  display: flex;
  gap: 1.5rem;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  display: flex;
  align-items: center;
  gap: 6px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.2s;
  &:hover {
    background: rgba(255, 255, 255, 0.07);
    color: #fff;
  }
`;

const PostStats = styled.div`
  display: flex;
  gap: 1rem;
  color: #bdbdbd;
  font-size: 0.9rem;
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const CommentsText = styled.span`
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;
